import { C, S } from "../customers/theme";
import { QT_STATUSES } from "../lib/quotations";
import { todayISO } from "../lib/dates";

const money = (n, cur = "€") => `${cur}${(Number(n) || 0).toFixed(2)}`;
const n3 = (x) => Math.round((Number(x) || 0) * 1000) / 1000;
const statusOf = (v) => QT_STATUSES.find((s) => s.value === v) || { label: v, color: C.textDim };

export function QTStatusBadge({ status }) {
  const s = statusOf(status);
  return <span style={S.pill(s.color)}>{s.label}</span>;
}

export default function QuotationView({ quote, busy, onClose, onEdit, onConvert, onDuplicate }) {
  const lines = (quote.quotation_lines || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
  const cust = quote.customers || {};
  const cur = quote.preferred_currency === "GBP" ? "£" : quote.preferred_currency === "USD" ? "$" : "€";
  const isConverted = quote.status === "converted" && quote.converted_sales_order_id;
  const expired = quote.expiry_date && quote.expiry_date < todayISO() && !["accepted", "converted", "rejected"].includes(quote.status);

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.modalBox, width: 860 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{quote.quotation_number}</h2>
            <div style={{ color: C.textMuted, fontSize: 13, marginTop: 3 }}>{quote.customer_name || cust.company_name || "—"}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <QTStatusBadge status={quote.status} />
            {!isConverted && <button onClick={() => onEdit(quote)} style={S.btn("secondary")}>Edit</button>}
            <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
          </div>
        </div>

        {isConverted && (
          <div style={{ marginTop: 10, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 13, color: C.textMuted }}>
            ✅ Converted to Sales Order <strong style={{ color: C.text, fontFamily: "monospace" }}>{quote.converted_sales_order_number || "—"}</strong>
          </div>
        )}
        {expired && (
          <div role="alert" style={{ marginTop: 10, background: C.warningDim, border: `1px solid ${C.warning}`, color: C.text, borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 600 }}>
            ⚠ This quotation expired on {quote.expiry_date}.
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 14 }}>
          <div>
            <div style={S.sectionTitle}>Customer</div>
            <div style={{ fontSize: 13, lineHeight: 1.7 }}>
              <div><strong>{quote.customer_name || cust.company_name || "—"}</strong></div>
              {quote.salesperson && <div style={{ color: C.textMuted }}>Salesperson: {quote.salesperson}</div>}
              {quote.reference && <div style={{ color: C.textMuted }}>Ref: {quote.reference}</div>}
            </div>
          </div>
          <div>
            <div style={S.sectionTitle}>Dates</div>
            <div style={{ fontSize: 13, lineHeight: 1.7, color: C.textMuted }}>
              <div>Issued: <span style={{ color: C.text }}>{quote.issue_date || "—"}</span></div>
              <div>Expires: <span style={{ color: expired ? C.warning : C.text }}>{quote.expiry_date || "—"}</span></div>
            </div>
          </div>
        </div>

        <div style={S.sectionTitle}>Line Items</div>
        <table style={S.table}>
          <thead><tr>{["Product", "SKU", "Qty", "Price", "Disc%", "VAT%", "Line Total"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {lines.length === 0 ? <tr><td colSpan={7} style={S.empty}>No lines</td></tr> : lines.map((l) => (
              <tr key={l.id}>
                <td style={S.td}>{l.description || "—"}</td>
                <td style={S.td}>{l.sku || "—"}</td>
                <td style={S.td}>{n3(l.quantity)}</td>
                <td style={S.td}>{money(l.unit_price, cur)}</td>
                <td style={S.td}>{n3(l.discount_percent)}</td>
                <td style={S.td}>{n3(l.vat_rate)}</td>
                <td style={S.td}>{money(l.line_total, cur)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <div style={{ background: C.surface2, borderRadius: 8, padding: 14, minWidth: 240 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Subtotal</span><strong>{money(quote.subtotal, cur)}</strong></div>
            {quote.total_discount > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Discount</span><strong>−{money(quote.total_discount, cur)}</strong></div>}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>VAT</span><strong>{money(quote.total_vat, cur)}</strong></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}><span>Total</span><span>{money(quote.total_amount, cur)}</span></div>
          </div>
        </div>

        {quote.notes && (<><div style={S.sectionTitle}>Notes</div><div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{quote.notes}</div></>)}

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button type="button" style={S.btn("secondary")} onClick={() => onDuplicate(quote)} disabled={busy}>⧉ Duplicate</button>
          {!isConverted && <button type="button" style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} onClick={() => onConvert(quote)} disabled={busy}>{busy ? "Converting…" : "Convert to Sales Order →"}</button>}
        </div>
      </div>
    </div>
  );
}
